/**
 * The global reactive store — "something in the database just changed, and
 * every screen that reads it has to know now".
 *
 * The application keeps its data in Dexie and its screens in React, and until
 * this module existed the two were joined by hand: whichever component ran a
 * mutation also called its own `reload()`, and every *other* screen carried on
 * showing what it had loaded on mount. Assign a convoy on the tasks board and
 * the roster still said "זמין" until it was navigated away from and back.
 *
 * So the stores announce their writes here instead. Each announcement carries
 * the domain that moved and bumps a revision counter; the data hooks subscribe
 * once and re-read, so a task closed in one tab is reflected in the dashboard
 * counters, the vehicle roster and the soldier roster on the same frame — with
 * no page reload and no pull-to-refresh.
 *
 * Three deliberate properties:
 *
 * - **Cheap and ignorable.** With no listener attached — the healthcheck, a
 *   test, the app before sign-in — announcing does nothing at all, so no store
 *   needs to guard the call.
 * - **Never fatal.** A listener that throws must not take the write down with
 *   it: the data is already saved by the time this runs.
 * - **Pure.** No React and no Dexie, so `src/core` stays executable outside the
 *   browser and the healthcheck can drive the bus directly.
 *
 * The cloud mirror's own signal (`./sync-signal`) is raised from here as well,
 * so a store that announces a change also pushes it — one call, both jobs.
 *
 * A third job joined them: an installed *broadcaster* (`./live-updates`) carries
 * the same announcement to every other window on the device, so two tabs on one
 * phone stay in step without waiting for a poll. It is installed by the shell
 * and absent everywhere else, which is why it is a hook here rather than an
 * import — this module stays pure and stays free of the browser.
 */
import { signalLocalWrite } from './sync-signal';

/** The parts of the database a screen can be reading. */
export const STORE_DOMAINS = [
  'tasks',
  'assignments',
  'fleet',
  'timeline',
  'audit',
  'auth',
  'settings',
] as const;

export type StoreDomain = (typeof STORE_DOMAINS)[number];

/** What a subscriber is handed: what moved, and how many writes deep we are. */
export interface StoreChange {
  domain: StoreDomain;
  /** Monotonic across every domain — a `useSyncExternalStore` snapshot. */
  revision: number;
}

export type StoreListener = (change: StoreChange) => void;

const listeners = new Set<StoreListener>();

let revision = 0;

/**
 * Where a local write is carried to besides this window — installed by
 * `startLiveUpdates()` in `./live-updates`, and `null` everywhere else.
 */
let broadcaster: StoreListener | null = null;

/**
 * Installs the cross-window broadcaster. Returns the uninstall, and refuses to
 * let a second install silently replace the first's teardown: uninstalling only
 * clears the hook if it is still the one that was put there.
 */
export function installStoreBroadcaster(listener: StoreListener): () => void {
  broadcaster = listener;
  return () => {
    if (broadcaster === listener) broadcaster = null;
  };
}

/**
 * How many writes this device has announced since it started. Monotonic, so it
 * is safe to use as the snapshot of an external store: two reads returning the
 * same number mean nothing changed in between.
 */
export function storeRevision(): number {
  return revision;
}

/**
 * Subscribes to every local write. Returns the unsubscribe, so a component that
 * mounts and unmounts repeatedly cannot leak a listener.
 */
export function subscribeToStore(listener: StoreListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Notifies this window's subscribers, and nothing else. */
function notify(change: StoreChange): void {
  for (const listener of [...listeners]) {
    try {
      listener(change);
    } catch {
      // The write stands; a listener's failure is not the writer's problem.
    }
  }
}

/**
 * Announces a write made *here*. Raises the cloud mirror's signal and the
 * cross-window broadcast too, so the change is pushed and shared as well as
 * painted — a store never has to remember to do all three.
 */
export function emitStoreChange(domain: StoreDomain): number {
  revision += 1;
  const change: StoreChange = { domain, revision };
  notify(change);
  signalLocalWrite();
  try {
    broadcaster?.(change);
  } catch {
    // The wire is a convenience; a failure on it never fails the write.
  }
  return revision;
}

/**
 * Announces a change that arrived from somewhere else — another window on this
 * device, or another operator's phone through the cloud mirror.
 *
 * The screens re-read exactly as they do for a local write, and the two signals
 * a local write raises are deliberately *not*: re-broadcasting would echo the
 * message back to the sender for ever, and raising the push signal would have
 * this device push a record it did not write.
 */
export function emitRemoteStoreChange(domain: StoreDomain): number {
  revision += 1;
  notify({ domain, revision });
  return revision;
}

/** Drops every subscriber — the healthcheck's own reset between suites. */
export function resetStoreListeners(): void {
  listeners.clear();
  broadcaster = null;
}
